import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import portalTypeCheckers from 'types/portalTypeCheckers';
import isDefined from 'isDefined';
import CurrentUserContext from 'contexts/CurrentUserContext';
import ImpersonatorContext from 'contexts/ImpersonatorContext';
import { getMappedEntityLinkId } from 'screens/EngagementPortalUsers/PortalMappedUsers';
import useSelectedEntityDefinition from 'screens/EntityDetailsShow/SelectedEntityProvider/useSelectedEntityDefinition';
import { CurrentUserContentSettingsContext } from './CurrentUserContentSettingsProvider';
import { PortalsContext } from './PortalsProvider';
import { ENTITY_APP_SIDEBAR_DROPDOWN_DIV_ID } from '../../../constants';
import ReactPortal from '../../../components/ReactPortal';
import { UIVersionContext } from './UIVersionProvider';
import { MappedUsersDropdown } from '../../../screens/EntityDetailsShow/TopBar';
import { V3SelectedAppContext } from './V3AppsProvider';
import useMappedUsers from '../../../screens/V3/Workspace/screens/UsersScreen/ProductAppUsersScreen/useMappedUsers';
import { getMappedEntityNavItem } from '../../../screens/V3/Workspace/screens/UsersScreen/ProductAppUsersScreen/MappedUsersList';
import { useLocation } from 'react-router-dom';

/**
 * This is a little different from {@link ImpersonatorContext}
 *
 * In {@link ImpersonatorContext}, we want to see the full experience of the
 * selected user. Here, we just want to filter an entity app according to a
 * mapped users settings
 *
 * This is to power a workflow where an EngagementPortal administrator can
 * switch between their users who have a simplified view and see what their
 * content will look like.
 *
 * It has been later modified to support allowing mapped engagement
 * users to switch between other users which they have been granted
 * access to.
 *
 * This is achieved in {@link useSelectedEntityFilter}, where we will read from
 * this context to build our filter.
 */
export const MappedUserContentSettingsContext = React.createContext<{
  selectedMappedUser?: MappedUser;
  onMappedUserSelected: (user: MappedUser | undefined) => void;
  /**
   * isMappedUserForCurrentEntity:
   * The filter against a {@link MappedUser} might not always be appropriate
   * for the currently selected entity details app.
   */
  isMappedUserForCurrentEntity: boolean;
  mappedUsersForCurrentPortal: MappedUser[];
  isLoadingMappedUsers: boolean;
}>({
  onMappedUserSelected: () => {},
  isMappedUserForCurrentEntity: false,
  mappedUsersForCurrentPortal: [],
  isLoadingMappedUsers: false,
});

const MappedUserContentSettingsProvider = ({
  children,
  mappedUsers,
  entityId,
  isLoadingMappedUsers,
}: {
  children: JSX.Element | JSX.Element[];
  mappedUsers: MappedUser[];
  entityId: string;
  isLoadingMappedUsers: boolean;
}) => {
  const { id: baseCurrentUserId } = useContext(CurrentUserContext);
  const { isV3 } = useContext(UIVersionContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { pathname } = useLocation();
  const currentUserId = impersonatorId ? impersonatorId : baseCurrentUserId;

  const { contentSettings } = useContext(CurrentUserContentSettingsContext);
  const [selectedMappedUser, setSelectedMappedUser] = React.useState<
    MappedUser | undefined
  >(() => {
    if (contentSettings.mode === 'normal') {
      return undefined;
    }

    if (!pathname.includes('/views/') && isV3) {
      return undefined;
    }

    return mappedUsers.find((mu) => mu.user.id === currentUserId);
  });

  useEffect(() => {
    if (contentSettings.mode === 'normal') {
      return;
    }

    if (!pathname.includes('/views/') && isV3) {
      // Do not force the mapped filters when viewing an app as opposed to a view
      return;
    }

    if (selectedMappedUser && selectedMappedUser.entityId === entityId) {
      return;
    }

    setSelectedMappedUser(
      mappedUsers.find((mu) => mu.user.id === currentUserId),
    );
  }, [
    contentSettings.mode,
    currentUserId,
    entityId,
    isV3,
    mappedUsers,
    pathname,
    selectedMappedUser,
  ]);

  const onMappedUserSelected = useCallback((user: MappedUser | undefined) => {
    setSelectedMappedUser(user);
  }, []);

  const mappedUsersForCurrentPortal = useMemo(() => {
    if (contentSettings.mode === 'normal') {
      return mappedUsers;
    }

    if (!pathname.includes('/views/') && isV3) {
      // Do not force the mapped filters when viewing an app as opposed to a view
      return mappedUsers;
    }

    const currentUserMapping = mappedUsers.find(
      (mu) => mu.user.id === currentUserId,
    );

    const mapping = contentSettings.mappings.find(
      (m) => m.entityId === entityId,
    );

    const otherMappedUsers = (mapping ? mapping.accessToOtherMappedUserIds : [])
      .map((uid) => mappedUsers.find((u) => u.user.id === uid))
      .filter(isDefined);

    return [currentUserMapping, ...otherMappedUsers].filter(isDefined);
  }, [contentSettings, currentUserId, entityId, isV3, mappedUsers, pathname]);

  return (
    <MappedUserContentSettingsContext.Provider
      value={{
        selectedMappedUser,
        onMappedUserSelected,
        isMappedUserForCurrentEntity: true,
        mappedUsersForCurrentPortal,
        isLoadingMappedUsers,
      }}
    >
      {children}
      {isV3 && (
        <ReactPortal elementId={ENTITY_APP_SIDEBAR_DROPDOWN_DIV_ID}>
          <MappedUsersDropdown />
        </ReactPortal>
      )}
    </MappedUserContentSettingsContext.Provider>
  );
};

const MappedUsersProvider = ({
  children,
  entityId,
  entityAppId,
}: {
  children: JSX.Element | JSX.Element[];
  entityId: string;
  entityAppId: string;
}) => {
  const { mappedUsers, isLoading } = useMappedUsers({ entityAppId, entityId });

  return (
    <MappedUserContentSettingsProvider
      mappedUsers={mappedUsers}
      entityId={entityId}
      isLoadingMappedUsers={isLoading}
    >
      {children}
    </MappedUserContentSettingsProvider>
  );
};

/**
 * If we are in an {@link EngagementPortal}, determine if the currently selected
 * entity app is the one this portal has users mapped against.
 * If so, load the possible mapped users and proceed.
 */
const MappedUserContentSettingsProviderGate = ({
  children,
  mappedUserPortal,
  entity,
  entityAppId,
}: {
  children: JSX.Element | JSX.Element[];
  mappedUserPortal?: EngagementPortal;
  entity: EntityDetails.Entity;
  entityAppId: string;
}) => {
  const { isV3 } = useContext(UIVersionContext);
  const { contentSettings } = useContext(CurrentUserContentSettingsContext);
  const { selectedPortal } = useContext(PortalsContext);
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  const selectedEntityDefinition = useSelectedEntityDefinition();
  const portal = mappedUserPortal ? mappedUserPortal : selectedPortal;

  const v2IsSelectedEntityExpectingMappedUsers = useMemo(() => {
    if (!portalTypeCheckers.isEngagementPortal(portal)) {
      return false;
    }

    const mappedEntityLink = getMappedEntityLinkId(portal, entity.id);
    if (!mappedEntityLink) {
      return false;
    }

    if (!selectedEntityDefinition) {
      return false;
    }

    if (contentSettings.mode === 'engagement') {
      if (
        !contentSettings.mappings.some(
          (mapping) => mapping.entityId === entity.id,
        )
      ) {
        return false;
      }
    }

    return selectedEntityDefinition.entity.id === mappedEntityLink.entityId;
  }, [portal, entity.id, selectedEntityDefinition, contentSettings]);

  const v3IsSelectedEntityExpectingMappedUsers = useMemo(() => {
    if (!selectedProductApp) {
      return false;
    }

    const mappedEntityLink = getMappedEntityNavItem(
      selectedProductApp,
      entity.id,
    );
    if (!mappedEntityLink) {
      return false;
    }

    if (!selectedEntityDefinition) {
      return false;
    }

    if (contentSettings.mode === 'engagement') {
      if (
        !contentSettings.mappings.some(
          (mapping) => mapping.entityId === entity.id,
        )
      ) {
        return false;
      }
    }

    return selectedEntityDefinition.entity.id === mappedEntityLink.entityId;
  }, [
    selectedProductApp,
    entity.id,
    selectedEntityDefinition,
    contentSettings,
  ]);

  if (isV3) {
    if (v3IsSelectedEntityExpectingMappedUsers || !selectedProductApp) {
      return <>{children}</>;
    }

    return (
      <MappedUsersProvider entityId={entity.id} entityAppId={entityAppId}>
        {children}
      </MappedUsersProvider>
    );
  }

  if (
    !v2IsSelectedEntityExpectingMappedUsers ||
    !portalTypeCheckers.isEngagementPortal(portal)
  ) {
    return <>{children}</>;
  }

  return (
    <MappedUsersProvider entityId={entity.id} entityAppId={entityAppId}>
      {children}
    </MappedUsersProvider>
  );
};

export default MappedUserContentSettingsProviderGate;
